import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import { GetServerSideProps } from 'next';
import TLC from '@/components/templates/Search/TLC';
import { APP_ROUTES } from '@/src/constants';
import { useTranslation } from '@/src/i18n';
import { getArticleBySlug } from '@/src/hooks/articles/fetchers';
import PreFooter from '@/components/organisms/PreFooter/PreFooter-v2';
import Breadcrumbs, {
  BreadcrumbItem,
} from '@/components/molecules/Breadcrumbs/Breadcrumbs';
import ArticleCardsList from '@/components/molecules/Seo/ArticleCardsList';
import { readingTime } from '@/src/utils';
import {
  filters2QueryParams,
  queryParams2InitialFilters,
} from '@/components/templates/Search/functions';

type Props = {
  articles: Article[];
};

const placeholderSpeciality = 'specialities';

const TLCList = ({ articles }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [filters, setFilters] = useState<Record<string, any>>({});

  // TODO: remove legacy (locality unecessary, initial value don't include some props (page, distanceMax and limit) and undefined props are written (newPatientFilter, address))
  const onFilterChange = newFilters => {
    const queryParams = filters2QueryParams(newFilters);

    const {
      locality,
      speciality = placeholderSpeciality,
      ...restQueryParams
    } = queryParams;
    let slug: string[];

    if (locality) {
      // The url structure is /[speciality]/[locality]
      // If user only filter by address (locality), for keep the url correctly, we need set value for speciality params too
      slug = [speciality, locality];
    } else if (speciality !== placeholderSpeciality) {
      slug = [speciality];
    } else {
      slug = [];
    }

    router.replace(
      {
        pathname: '/teleconsultation/sans-rdv/recherche/[[...slug]]',
        query: {
          ...router.query,
          ...restQueryParams,
          slug,
        },
      },
      undefined,
      { shallow: true },
    );
    setFilters(newFilters);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      name: 'Maiia',
      url: APP_ROUTES.HOME,
    },
    {
      name: t('teleconsultation'),
      url: APP_ROUTES.TLC.BASE_URL,
    },
  ];

  if (filters.address?.city)
    breadcrumbs.push({
      name: filters.address.city,
      url: `/teleconsultation/${filters.address?.zipCode ||
        filters.address?.citycode}-${filters.address?.shortName}`,
    });

  // Init filters
  React.useEffect(() => {
    setFilters(queryParams2InitialFilters(router.query));
  }, [router.query]);

  return (
    <>
      <NextSeo
        title={t('seo-tlc-direct-title')}
        description={t('seo-tlc-direct-description')}
      />

      <TLC filters={filters} onFilterChange={onFilterChange} />

      <PreFooter className="max-w-[1024px]">
        <div className="flex justify-center">
          <Breadcrumbs items={breadcrumbs} className="max-w-[760px]" />
        </div>
        <ArticleCardsList articles={articles} />
      </PreFooter>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  req,
}) => {
  let relatedArticlesWithReadingTime: Article[] | null = null;
  try {
    const isSncfUrl = req.headers.host?.includes('sncf');

    if (isSncfUrl) {
      return {
        redirect: {
          destination: APP_ROUTES.HOME,
          permanent: false,
        },
      };
    }

    // Get suggested articles
    const article = await getArticleBySlug('teleconsultation');
    relatedArticlesWithReadingTime = article?.relatedArticles.map(
      relatedArticle => ({
        ...relatedArticle,
        readingTime: readingTime(relatedArticle),
      }),
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('error prefetching article', err);
  }

  return {
    props: { articles: relatedArticlesWithReadingTime || [] },
  };
};

export default TLCList;
